import { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { message } from "antd";
import { useAppSelector, useAppDispatch } from "./reduxHook";
import { selectEnrichedCatalogItems } from "../reducers/catalogItemsSlice";
import { CatalogItem } from "../types/api/models/catalog";
import { fetchImageAsBlob, generateUniqueCatalogId } from "../utils";
import { clearSelection } from "../reducers/catalogSelectionSlice";

export const CSV_SCHEMA = [
  {
    header: "Image",
    getValue: (item: CatalogItem) => {
      return `${generateUniqueCatalogId(item.source, item.id)}.jpg`;
    },
  },
  {
    header: "UUID",
    getValue: (item: CatalogItem) => item.id,
  },
  { header: "Image Type", getValue: (item: CatalogItem) => item.imageType },
  {
    header: "Created At",
    getValue: (item: CatalogItem) =>
      item.created_at ? new Date(item.created_at).toISOString() : "",
  },
  {
    header: "Device Name",
    getValue: (item: CatalogItem) => item.device_name,
  },
  {
    header: "Trap Site Name",
    getValue: (item: CatalogItem) => item.trap_site_name,
  },
  {
    header: "Verified Species",
    getValue: (item: CatalogItem) =>
      item.source === "detection" ? item?.contributed_species : "",
  },
];

export const generateCatalogCSV = (items: CatalogItem[]): string => {
  const headers = CSV_SCHEMA.map((s) => s.header).join(",");

  const rows = items.map((item) =>
    CSV_SCHEMA.map((field) => {
      const value = field.getValue(item);
      const str = value === null || value === undefined ? "" : String(value);
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    }).join(","),
  );

  return [headers, ...rows].join("\n");
};

export const DOWNLOAD_CONFIG = {
  IMAGES_FOLDER: "images",
  CSV_NAME: "catalog_data.csv",
  ZIP_PREFIX: "scout_catalog_",
};

export const useCatalogDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const enrichedItems = useAppSelector(selectEnrichedCatalogItems);
  const isAvailable = enrichedItems.length > 0;
  const dispatch = useAppDispatch();

  const handleDownload = async () => {
    if (!isAvailable || isDownloading) return;

    try {
      setIsDownloading(true);
      const zip = new JSZip();
      const imagesFolder = zip.folder(DOWNLOAD_CONFIG.IMAGES_FOLDER);

      if (!imagesFolder) {
        throw new Error("Failed to create images folder in ZIP");
      }

      // Add images
      const fetchPromises = enrichedItems.map(async (item) => {
        const filename = `${generateUniqueCatalogId(item.source, item.id)}.jpg`;

        try {
          const blob = await fetchImageAsBlob(item.img);
          imagesFolder.file(filename, blob);
        } catch (error) {
          message.error(`Error adding image ${filename} to zip:`);
        }

        try {
          const blob = await fetchImageAsBlob(item.img);
          const filename = `${generateUniqueCatalogId(item.source, item.id)}.jpg`;
          imagesFolder.file(filename, blob);
        } catch (error) {
          message.error(
            `Error adding image ${generateUniqueCatalogId(item.source, item.id)} to zip:`,
          );
        }
      });

      await Promise.all(fetchPromises);

      // Add CSV
      const csvContent = generateCatalogCSV(enrichedItems);
      zip.file(DOWNLOAD_CONFIG.CSV_NAME, csvContent);

      // Generate and save zip
      const content = await zip.generateAsync({ type: "blob" });
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      saveAs(content, `${DOWNLOAD_CONFIG.ZIP_PREFIX}${timestamp}.zip`);
    } catch (error) {
      message.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
      dispatch(clearSelection());
    }
  };

  return {
    handleDownload,
    isDownloading,
    isAvailable,
    selectedCount: enrichedItems.length,
  };
};
